import { useState, useEffect, useCallback } from 'react';

interface RefreshLimiterOptions {
    cooldownTime?: number;       // Tiempo de espera entre refrescos (ms)
    maxRefreshCount?: number;    // Número máximo de refrescos permitidos
    timeWindow?: number;         // Ventana de tiempo para contar refrescos (ms)
    penaltyTime?: number;        // Tiempo de penalización si se excede el límite (ms)
}

interface RefreshLimiterReturn {
    isDisabled: boolean;         // Si el refresco está deshabilitado
    hasReachedLimit: boolean;    // Si se ha alcanzado el límite de refrescos
    cooldownRemaining: number;   // Tiempo restante de enfriamiento (ms)
    refreshCount: number;        // Número de refrescos realizados en la ventana actual
    executeRefresh: (callback: () => void) => void; // Función para ejecutar el refresco
    resetLimiter: () => void;    // Función para reiniciar el limitador
}

/**
 * Hook para controlar la frecuencia de refresco y prevenir abusos
 */
const useRefreshLimiter = ({
    cooldownTime = 10000,      // 10 segundos por defecto
    maxRefreshCount = 5,       // 5 refrescos como máximo
    timeWindow = 60000,        // En un periodo de 1 minuto
    penaltyTime = 300000       // 5 minutos de penalización
}: RefreshLimiterOptions = {}): RefreshLimiterReturn => {
    const [isDisabled, setIsDisabled] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState(0);
    const [refreshHistory, setRefreshHistory] = useState<number[]>([]);
    const [hasReachedLimit, setHasReachedLimit] = useState(false);

    // Limpiar historial antiguo
    useEffect(() => {
        const cleanupInterval = setInterval(() => {
            const now = Date.now();
            setRefreshHistory(prev => prev.filter(time => now - time < timeWindow));
        }, 10000); // Limpiar cada 10 segundos

        return () => clearInterval(cleanupInterval);
    }, [timeWindow]);

    // Función para reiniciar el limitador
    const resetLimiter = useCallback(() => {
        setIsDisabled(false);
        setCooldownRemaining(0);
        setRefreshHistory([]);
        setHasReachedLimit(false);
    }, []);

    // Función para ejecutar el refresco con las limitaciones
    const executeRefresh = useCallback((callback: () => void) => {
        if (isDisabled) return;

        const now = Date.now();

        // Agregar este refresco al historial
        const newHistory = [...refreshHistory, now];

        // Filtrar para mantener solo los refrescos dentro de la ventana de tiempo
        const recentRefreshes = newHistory.filter(time => now - time < timeWindow);

        // Verificar si se ha alcanzado el límite de refrescos
        if (recentRefreshes.length > maxRefreshCount) {
            setHasReachedLimit(true);
            setIsDisabled(true);

            // Desbloquear después del tiempo de penalización
            setTimeout(() => {
                setHasReachedLimit(false);
                setIsDisabled(false);
                setRefreshHistory([]);
            }, penaltyTime);

            return;
        }

        // Actualizar historial de refrescos
        setRefreshHistory(recentRefreshes);

        // Ejecutar la función de refresco
        callback();

        // Aplicar tiempo de enfriamiento
        setIsDisabled(true);
        setCooldownRemaining(cooldownTime);

        // Iniciar temporizador para el tiempo de enfriamiento
        const cooldownInterval = setInterval(() => {
            setCooldownRemaining(prev => {
                if (prev <= 1000) {
                    clearInterval(cooldownInterval);
                    setIsDisabled(false);
                    return 0;
                }
                return prev - 1000;
            });
        }, 1000);
    }, [isDisabled, refreshHistory, cooldownTime, maxRefreshCount, timeWindow, penaltyTime]);

    return {
        isDisabled,
        hasReachedLimit,
        cooldownRemaining,
        refreshCount: refreshHistory.length,
        executeRefresh,
        resetLimiter
    };
};

export default useRefreshLimiter;